// apps/flux-ems-web/src/components/simulation/DailyPlaybackTab.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Box, Typography, Paper, Grid, Select, MenuItem, IconButton,
    Slider, Chip,
} from '@mui/material';
import {
    PlayArrow as PlayIcon,
    Pause as PauseIcon,
    SkipNext as SkipIcon,
    SkipPrevious as PrevIcon,
} from '@mui/icons-material';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid,
    Tooltip as RechartsTooltip, ResponsiveContainer,
    ReferenceLine,
} from 'recharts';
import AgentNetworkDiagram from './AgentNetworkDiagram';

const GOLD = '#b28c50';
const GOLD_LIGHT = '#d4a574';
const HEADING_FONT = "'Syne', sans-serif";
const MONO_FONT = "'JetBrains Mono', monospace";

// Agent colors
const AGENT_COLORS = {
    battery: { solid: '#b28c50', faded: 'rgba(178,140,80,0.3)' },
    site_dna: { solid: '#b28c50', faded: 'rgba(178,140,80,0.3)' },
    ev_fleet: { solid: '#d4a574', faded: 'rgba(212,165,116,0.3)' },
    cold_storage: { solid: '#a6a19b', faded: 'rgba(166,161,155,0.3)' },
};

const REASON_LABELS = {
    highest_peak: 'Highest Peak Day',
    highest_savings: 'Highest Savings Day',
    typical_summer: 'Typical Summer Day',
    typical_winter: 'Typical Winter Day',
    most_coordinated: 'Most Coordinated Day',
};

function stepToTime(step) {
    const hours = Math.floor(step / 4);
    const mins = (step % 4) * 15;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Daily Playback Tab — animated dual stacked area with agent network.
 *
 * @param {Object} props
 * @param {Array} props.dailyProfiles - Array of daily profile objects from results
 * @param {number} props.siteCapacityKw - Site capacity limit for reference line
 * @param {Object} props.agents - { battery, ev_fleet, cold_storage } config booleans
 * @param {Object} props.themeColors - From useThemeColors()
 */
export default function DailyPlaybackTab({ dailyProfiles, siteCapacityKw, agents, themeColors }) {
    const { isRetro, CARD_BG, TEXT_PRIMARY, TEXT_SECONDARY, GOLD_BORDER } = themeColors;

    const [selectedDay, setSelectedDay] = useState(0);
    const [playhead, setPlayhead] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [speed, setSpeed] = useState(1);

    const intervalRef = useRef(null);

    const profile = dailyProfiles?.[selectedDay];
    const steps = profile?.steps || [];
    const agentIds = steps.length > 0
        ? Object.keys(steps[0].siloed).filter(k => k !== 'total')
        : [];

    // Playback timer
    useEffect(() => {
        if (playing && steps.length > 0) {
            const ms = Math.max(30, 100 / speed);
            intervalRef.current = setInterval(() => {
                setPlayhead((prev) => {
                    if (prev >= steps.length - 1) {
                        setPlaying(false);
                        return prev;
                    }
                    return prev + 1;
                });
            }, ms);
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [playing, speed, steps.length]);

    // Reset playhead on day change
    useEffect(() => {
        setPlayhead(0);
        setPlaying(false);
    }, [selectedDay]);

    const togglePlay = useCallback(() => {
        if (playhead >= steps.length - 1) {
            setPlayhead(0);
        }
        setPlaying((p) => !p);
    }, [playhead, steps.length]);

    // Build chart data — separate siloed and orchestrated
    const { siloedChartData, orchChartData } = useMemo(() => {
        if (!steps.length) return { siloedChartData: [], orchChartData: [] };

        const siloedData = steps.map((s, i) => {
            const point = { time: stepToTime(i), step: i };
            agentIds.forEach(aid => {
                point[aid] = s.siloed[aid] || 0;
            });
            point.total = s.siloed.total || 0;
            return point;
        });

        const orchData = steps.map((s, i) => {
            const point = { time: stepToTime(i), step: i };
            agentIds.forEach(aid => {
                point[aid] = s.orchestrated[aid] || 0;
            });
            point.total = s.orchestrated.total || 0;
            point.isPast = i <= playhead;
            return point;
        });

        return { siloedChartData: siloedData, orchChartData: orchData };
    }, [steps, playhead, agentIds]);

    // Current step info
    const currentStep = steps[playhead] || {};
    const currentCoord = currentStep.coordination || {};

    // Network coordination intensity
    const coordIntensity = currentCoord.lp_active ? 0.8 : 0.1;

    // Activity feed — collect recent coordination events
    const recentEvents = useMemo(() => {
        const events = [];
        const start = Math.max(0, playhead - 8);
        for (let i = start; i <= playhead; i++) {
            const s = steps[i];
            if (!s) continue;
            if (s.coordination?.lp_active) {
                const time = stepToTime(i);
                events.push(`[${time}] LP coordination active \u2014 ${s.coordination.iterations} rounds`);
                agentIds.forEach(aid => {
                    const sVal = s.siloed[aid] || 0;
                    const oVal = s.orchestrated[aid] || 0;
                    const diff = sVal - oVal;
                    if (Math.abs(diff) > 5) {
                        const label = aid.replace('_', ' ');
                        const action = diff > 0 ? `reduced ${Math.round(diff)} kW` : `increased ${Math.round(-diff)} kW`;
                        events.push(`[${time}] ${label}: ${action} (${Math.round(sVal)} -> ${Math.round(oVal)} kW)`);
                    }
                });
            }
        }
        return events.slice(-12);
    }, [playhead, steps, agentIds]);

    if (!dailyProfiles || dailyProfiles.length === 0) {
        return (
            <Paper sx={{ p: 4, textAlign: 'center', background: CARD_BG, border: `1px solid ${GOLD_BORDER}` }}>
                <Typography sx={{ color: TEXT_SECONDARY }}>No daily profiles available for this run.</Typography>
            </Paper>
        );
    }

    const renderChart = (data, title, muted) => (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: muted ? TEXT_SECONDARY : GOLD, fontFamily: HEADING_FONT, mb: 0.5 }}>
                {title}
            </Typography>
            <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(178,140,80,0.08)" />
                    <XAxis
                        dataKey="time"
                        tick={{ fill: TEXT_SECONDARY, fontSize: 10, fontFamily: MONO_FONT }}
                        interval={15}
                    />
                    <YAxis
                        tick={{ fill: TEXT_SECONDARY, fontSize: 10, fontFamily: MONO_FONT }}
                        tickFormatter={(v) => `${v}`}
                        label={{ value: 'kW', position: 'insideLeft', fill: TEXT_SECONDARY, fontSize: 10 }}
                    />
                    {agentIds.map((aid) => (
                        <Area
                            key={aid}
                            type="monotone"
                            dataKey={aid}
                            stackId="1"
                            fill={muted
                                ? 'rgba(136,136,136,0.3)'
                                : (AGENT_COLORS[aid]?.solid || GOLD)
                            }
                            stroke={muted ? '#666' : (AGENT_COLORS[aid]?.solid || GOLD)}
                            fillOpacity={muted ? 0.4 : 0.6}
                            strokeWidth={1}
                        />
                    ))}
                    {siteCapacityKw && (
                        <ReferenceLine
                            y={siteCapacityKw}
                            stroke="rgba(211,47,47,0.5)"
                            strokeDasharray="5 5"
                            label={{ value: `${siteCapacityKw} kW limit`, fill: 'rgba(211,47,47,0.6)', fontSize: 10 }}
                        />
                    )}
                    <ReferenceLine
                        x={stepToTime(playhead)}
                        stroke={GOLD}
                        strokeWidth={2}
                    />
                    <RechartsTooltip
                        contentStyle={{ background: CARD_BG, border: `1px solid ${GOLD_BORDER}`, fontFamily: MONO_FONT, fontSize: 11 }}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </Box>
    );

    return (
        <Box>
            {/* Controls bar */}
            <Paper sx={{ p: 2, mb: 3, background: CARD_BG, border: `1px solid ${GOLD_BORDER}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {/* Day picker */}
                    <Select
                        value={selectedDay}
                        onChange={(e) => setSelectedDay(e.target.value)}
                        size="small"
                        sx={{
                            minWidth: 240,
                            color: TEXT_PRIMARY,
                            fontFamily: MONO_FONT,
                            fontSize: '0.85rem',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: GOLD_BORDER },
                        }}
                    >
                        {dailyProfiles.map((dp, idx) => (
                            <MenuItem key={idx} value={idx}>
                                {dp.date_label} \u2014 {REASON_LABELS[dp.reason] || dp.reason}
                            </MenuItem>
                        ))}
                    </Select>

                    {/* Playback controls */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => setPlayhead(Math.max(0, playhead - 4))} sx={{ color: GOLD }}>
                            <PrevIcon fontSize="small" />
                        </IconButton>
                        <IconButton onClick={togglePlay} sx={{ color: '#000', background: GOLD, '&:hover': { background: GOLD_LIGHT } }}>
                            {playing ? <PauseIcon /> : <PlayIcon />}
                        </IconButton>
                        <IconButton size="small" onClick={() => setPlayhead(Math.min(95, playhead + 4))} sx={{ color: GOLD }}>
                            <SkipIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Speed control */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {[1, 2, 4].map((s) => (
                            <Chip
                                key={s}
                                label={`${s}x`}
                                size="small"
                                onClick={() => setSpeed(s)}
                                sx={{
                                    fontFamily: MONO_FONT,
                                    color: speed === s ? '#000' : TEXT_SECONDARY,
                                    background: speed === s ? GOLD : 'transparent',
                                    borderColor: GOLD_BORDER,
                                    cursor: 'pointer',
                                }}
                                variant={speed === s ? 'filled' : 'outlined'}
                            />
                        ))}
                    </Box>

                    {/* Time display */}
                    <Typography sx={{ color: GOLD, fontFamily: MONO_FONT, fontSize: '1rem', fontWeight: 700, ml: 'auto' }}>
                        {stepToTime(playhead)}
                    </Typography>
                </Box>

                {/* Scrub slider */}
                <Slider
                    value={playhead}
                    onChange={(_, v) => { setPlayhead(v); setPlaying(false); }}
                    min={0}
                    max={95}
                    sx={{
                        mt: 1.5, color: GOLD,
                        '& .MuiSlider-thumb': { width: 12, height: 12 },
                        '& .MuiSlider-rail': { opacity: 0.2 },
                    }}
                />
            </Paper>

            <Grid container spacing={3}>
                {/* Left: dual charts */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 2.5, background: CARD_BG, border: `1px solid ${GOLD_BORDER}` }}>
                        {renderChart(siloedChartData, 'Siloed \u2014 Independent Operation', true)}
                        {renderChart(orchChartData, 'Orchestrated \u2014 Vortx Coordinated', false)}
                    </Paper>

                    {/* Activity feed */}
                    {recentEvents.length > 0 && (
                        <Paper sx={{
                            p: 2, mt: 2, background: isRetro ? '#1a1a1a' : '#f5f5f5', border: `1px solid ${GOLD_BORDER}`,
                            maxHeight: 180, overflow: 'auto',
                        }}>
                            <Typography variant="subtitle2" sx={{ color: GOLD, fontFamily: HEADING_FONT, mb: 0.5, fontSize: '0.8rem' }}>
                                Coordination Log
                            </Typography>
                            {recentEvents.map((line, i) => (
                                <Typography
                                    key={i}
                                    sx={{
                                        color: line.includes('reduced') ? GOLD_LIGHT
                                            : line.includes('LP') ? GOLD
                                            : 'rgba(244,241,236,0.5)',
                                        fontFamily: MONO_FONT,
                                        fontSize: '0.7rem',
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {line}
                                </Typography>
                            ))}
                        </Paper>
                    )}
                </Grid>

                {/* Right: agent network + step info */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, mb: 2, background: CARD_BG, border: `1px solid ${GOLD_BORDER}`, display: 'flex', justifyContent: 'center' }}>
                        <AgentNetworkDiagram
                            phase="playback"
                            agents={agents}
                            coordinationIntensity={coordIntensity}
                            signals={
                                currentCoord.lp_active ? Object.fromEntries(
                                    agentIds.map(aid => {
                                        const oVal = currentStep.orchestrated?.[aid] || 0;
                                        return [aid, `${Math.round(oVal)} kW`];
                                    })
                                ) : {}
                            }
                            compact
                        />
                    </Paper>

                    {/* Current step summary */}
                    <Paper sx={{ p: 2, background: CARD_BG, border: `1px solid ${GOLD_BORDER}` }}>
                        <Typography variant="subtitle2" sx={{ color: TEXT_PRIMARY, fontFamily: HEADING_FONT, mb: 1 }}>
                            Step {playhead} \u2014 {stepToTime(playhead)}
                        </Typography>
                        {agentIds.map(aid => {
                            const sVal = currentStep.siloed?.[aid] || 0;
                            const oVal = currentStep.orchestrated?.[aid] || 0;
                            const diff = sVal - oVal;
                            return (
                                <Box key={aid} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: `1px solid ${GOLD_BORDER}` }}>
                                    <Typography sx={{ color: TEXT_SECONDARY, fontFamily: MONO_FONT, fontSize: '0.75rem' }}>
                                        {aid.replace('_', ' ')}
                                    </Typography>
                                    <Typography sx={{ fontFamily: MONO_FONT, fontSize: '0.75rem', color: diff > 5 ? '#2e7d32' : diff < -5 ? '#d32f2f' : TEXT_PRIMARY }}>
                                        {Math.round(sVal)} \u2192 {Math.round(oVal)} kW
                                        {Math.abs(diff) > 5 && ` (${diff > 0 ? '-' : '+'}${Math.abs(Math.round(diff))})`}
                                    </Typography>
                                </Box>
                            );
                        })}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, mt: 0.5 }}>
                            <Typography sx={{ color: TEXT_PRIMARY, fontFamily: MONO_FONT, fontSize: '0.8rem', fontWeight: 700 }}>
                                Total
                            </Typography>
                            <Typography sx={{ color: GOLD, fontFamily: MONO_FONT, fontSize: '0.8rem', fontWeight: 700 }}>
                                {Math.round(currentStep.siloed?.total || 0)} \u2192 {Math.round(currentStep.orchestrated?.total || 0)} kW
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
